/* eslint-disable max-len */
import { CheckOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';
import type { Field } from '@evidia/components';
import { STUDENT_ADDRESS_FIELDS, SimpleForm } from '@evidia/components';
import { toCzDateString } from '@evidia/shared';
import { Button, notification } from 'antd';
import type { Dayjs } from 'dayjs';
import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { STUDENT_INFO, STUDENT_INFO_SERVER, STUDENT_STUDY_INFO_NOTE } from '../../const/forms';
import { useSystemStore } from '../../stores/SystemStore';
import { trpc } from '../../utils/trcp';
import InterpreterExtraFormLink from '../InterpreterExtraFormLink';

type EditableType = 'studentInfo' | 'studentAddress' | 'studentStudyInfo' | 'student' | 'log' | 'studentInterpreter';

type EditableValue = string | Dayjs | null | undefined;

type EditableSingleItemBase = {
	property: string;
	value: EditableValue;

	border?: boolean;
};

type EditableSingleMultipleBase = {
	property: string[];
	value: Record<string, EditableValue>;

	border?: boolean;
};

const Item = styled.div<{ $border: boolean; $undefined?: boolean }>`
	padding-top: ${props => (props.$border ? '1em' : 'none')};
	border-bottom: ${props => (props.$border ? '1px solid #f0f0f0;' : 'none')};
	display: flex;
	justify-content: space-between;
	flex-direction: row;
	align-items: center;

	.edit-icon {
		opacity: 0;
	}

	&:hover {
		.edit-icon {
			opacity: 1;
		}
	}

	&:first-child {
		border-bottom: none;
	}

	&:last-child {
		border-bottom: none;
	}

	font-size: 14px;
	line-height: 18px;
	color: #000000;

	${({ $undefined }) =>
		$undefined &&
		css`
			font-size: 12px;
			line-height: 16px;
		`}

	p {
		padding-bottom: 1em;
		min-height: 16px;
	}
`;

const BtnsContainer = styled.div`
	display: flex;
	gap: 5px;
`;

type Editable = {
	type: EditableType;
	studentId: string;
	schoolId: string;
	onDone: () => Promise<any>;
	meta?: Record<string, any>;
	data?: {
		getInterpreters: {
			data: {
				id: string;
				interpreterInfo: {
					firstName: string;
					lastName: string;
				};
			}[];
			isLoading: boolean;
		};
	};
} & (EditableSingleItemBase | EditableSingleMultipleBase);

const HoverableEditItem: React.FC<{
	children: AnyReactElement;
	valueUndefined?: boolean;
	form?: Editable;
	noBorder?: boolean;
}> = ({ children, form, valueUndefined, noBorder = false }) => {
	const hasBorder = (form?.border ?? true) && !noBorder;
	const updateStudent = trpc.student.updateStudent.useMutation();

	const updateStudentLog = trpc.student.updateStudentLog.useMutation();
	const [isEditing, setIsEditing] = useState(false);
	const incrementDrawerVersion = useSystemStore(state => state.incrementDrawerVersion);

	if (form && isEditing) {
		const fieldsMap: Record<EditableType, Field[]> = {
			studentInfo: STUDENT_INFO,
			studentAddress: STUDENT_ADDRESS_FIELDS,
			studentStudyInfo: [
				{
					key: 'carIdNumber',
					label: 'Číslo řidičského průkazu',
					type: 'string',
					rules: [{ required: true, message: 'Prosím zadejte číslo řidičského průkazu!' }],
					placeholder: 'EA000000',
					required: true,
				},
				{
					key: 'note',
					label: 'Poznámka',
					type: 'string',
					placeholder: 'Poznámka',
					required: false,
					rules: [STUDENT_STUDY_INFO_NOTE],
				},
			],
			student: [
				...STUDENT_INFO_SERVER,
				{
					key: 'dateOfFinished',
					label: 'Datum ukončení',
					placeholder: '20.03.2023',
					type: 'day-picker',
					required: false,
				},
			],
			log: [
				{
					key: 'date',
					label: 'Datum',
					rules: [{ required: true, message: 'Prosím zadejte datum!' }],
					placeholder: '20.03.2023',
					type: 'day-picker',
					required: true,
				},
			],
			studentInterpreter: [
				{
					key: 'interpreter',
					label: 'Tlumočník',
					placeholder: 'František Novák',
					required: false,
					type: 'select',
					values:
						form.data?.getInterpreters.data?.map(i => ({
							value: `${i.interpreterInfo?.firstName} ${i.interpreterInfo?.lastName}`,
							key: i.id,
						})) ?? [],
					loading: form.data?.getInterpreters.isLoading,
					extra: <InterpreterExtraFormLink />,
				},
			],
		};

		const targetProperty = Array.isArray(form.property) ? form.property : [form.property];
		const fields = fieldsMap[form.type].filter(field => form.property.includes(field.key));
		const initialFields: any = !Array.isArray(form.property)
			? { [form.property as string]: form.value }
			: form.value;

		if (fields) {
			const dataTestId = `${form.type}-${Array.isArray(form.property) ? form.property.join('-') : form.property}`;
			// eslint-disable-next-line no-console
			return (
				<>
					{children[0]}
					<SimpleForm
						hideLabels
						lessSpaceBetweenItems
						inline
						fields={fields}
						onFinish={async (v: Record<string, any>) => {
							const payload = targetProperty.reduce((acc: Record<string, any>, curr) => {
								acc[curr] = v[curr];
								return acc;
							}, {});
							const isSame = JSON.stringify(payload) === JSON.stringify(initialFields);
							if (isSame) {
								notification.warning({
									description: 'Nebyly provedeny žádné změny!',
									message: 'Upozornění',
									closeIcon: false,
								});
								setIsEditing(false);
								return;
							}
							if (form.type === 'log') {
								const { meta } = form;
								await updateStudentLog.mutateAsync({
									studentId: form.studentId,
									schoolId: form.schoolId,
									id: meta!.id,
									date: toCzDateString(payload.date),
								});
								await form.onDone();
								incrementDrawerVersion();
								setIsEditing(false);
								return;
							}
							try {
								await updateStudent.mutateAsync({
									studentId: form.studentId,
									schoolId: form.schoolId,
									// [form.type]: {
									// 	[form.property]: v[form.property],
									// },
									[form.type]: payload,
								});
							} catch {
								if (form.property === 'registrationNumber') {
									notification.error({
										description: 'Uživatel s tímto evidenčním číslem již existuje!',
										message: 'Nastala chyba',
										closeIcon: false,
									});
									return;
								}
							}
							await form.onDone();
							incrementDrawerVersion();
							setIsEditing(false);
						}}
						initialValues={initialFields}
						customButton={
							<BtnsContainer>
								<Button
									size="large"
									onClick={() => setIsEditing(false)}
									data-testId={`${dataTestId}-cancel-btn`}
								>
									<CloseOutlined />
								</Button>
								<Button
									size="large"
									type="primary"
									htmlType="submit"
									data-testId={`${dataTestId}-save-btn`}
									loading={updateStudent.isLoading}
								>
									<CheckOutlined />
								</Button>
							</BtnsContainer>
						}
					/>
				</>
			);
		}
	}

	if (form) {
		const dataTestId = `${form.type}-${Array.isArray(form.property) ? form.property.join('-') : form.property}`;
		return (
			<Item $border={hasBorder} $undefined={valueUndefined} data-testid={dataTestId}>
				<div>{children}</div>
				<Button
					className="edit-icon"
					data-testId={`${dataTestId}-edit-btn`}
					icon={<EditOutlined />}
					type="text"
					onClick={() => setIsEditing(true)}
				/>
			</Item>
		);
	}

	return (
		<Item $border={hasBorder} $undefined={valueUndefined}>
			<div>{children}</div>
		</Item>
	);
};

export default HoverableEditItem;
